import {
    api,
    ApiError,
    apiErrorMessage,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    ConfirmDialog,
    Money,
    PageHeader,
    PublicId,
    ReasonDialog,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { Charge, StaffCharge } from '@/api/types';
import {
    ChargeFacts,
    ChargeHttpCalls,
    ChargeTimeline,
} from '@/components/charge';
import { detailTitle } from '@/components/detail-title';
import { canRefund, RefundSheet, RefundView } from '@/components/refund-sheet';
import {
    useChargeSubjects,
    WebhookDeliveries,
} from '@/components/webhook-deliveries';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';

/** The reads of `/money/charges/:id` (BM-46 §2.0). */
export const chargeReads = (id: string | undefined) => ({
    charge: oneSpec(`/staff/charges/${id ?? ''}`),
    history: oneSpec(`/staff/charges/${id ?? ''}/history`),
});

type Kind = 'capture' | 'void';

/**
 * BM-51 § 8: one charge with its staff timeline; a held charge (`authorized`, so no open review) is captured or
 * voided directly. The page sends no amount: a capture is the full authorized amount, decided by the server. One
 * Idempotency-Key per dialog, as on the review decision page: minted when it opens, kept for a resubmit after no
 * response, replaced after any HTTP error response, whose message stays inside the dialog.
 */
export function ChargePage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const query = useOne<StaffCharge>(chargeReads(id).charge.path);
    const [action, setAction] = useState<{ kind: Kind; key: string } | null>(
        null,
    );
    const [refunding, setRefunding] = useState(false);
    const [viewingRefunds, setViewingRefunds] = useState(false);
    const act = useWrite(
        async (body: { kind: Kind; key: string; reason?: string }) =>
            (
                await api().post<Charge>(
                    `/staff/charges/${id}/${body.kind}`,
                    body.kind === 'void' ? { reason: body.reason } : undefined,
                    { idempotencyKey: body.key },
                )
            ).data,
        ['/staff/charges', '/staff/charge-reviews', '/staff/queues'],
    );
    const open = (kind: Kind) => {
        act.reset();
        setAction({ kind, key: crypto.randomUUID() });
    };
    const send = (a: { kind: Kind; key: string }, reason?: string) =>
        act.mutate(
            { ...a, reason },
            {
                onSuccess: () => setAction(null),
                // no response (a network error): the key stays, so the resubmit replays the same action
                onError: (e) =>
                    ApiError.is(e)
                        ? setAction((d) =>
                              d ? { ...d, key: crypto.randomUUID() } : d,
                          )
                        : undefined,
            },
        );
    const c = query.data?.data;
    // BM-84 Q5: the capture and void routes need payments.charges.manage (the review decision stays reviews.decide)
    const decidable = c?.state === 'authorized' && can(P.chargesManage);
    // bm-111 § 11: a held charge is captured or voided, a captured one refunded, so the actions never meet
    // bm-146 § 10: only the Issue a refund action keeps the canRefund gate; every other charge opens the refund view
    const refundable = c !== undefined && canRefund(c, can);
    // bm-147 § 8 (D-20): order and charge are one to one, so the panel shows the order page's set
    const subjects = useChargeSubjects(
        c && {
            orderId: c.order_id,
            chargeId: c.id,
            refunds: c.refunds ?? [],
        },
    );
    const viewRefunds = () => setViewingRefunds(true);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Charges', href: '/money/charges' }]}
                {...detailTitle(query, null, id, 'Charge')}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    decidable ? (
                        <Button onClick={() => open('capture')}>Capture</Button>
                    ) : refundable ? (
                        <Button onClick={() => setRefunding(true)}>
                            Refund
                        </Button>
                    ) : c ? (
                        <Button variant="outline" onClick={viewRefunds}>
                            Refunds
                        </Button>
                    ) : null
                }
                secondaryAction={
                    decidable ? (
                        <Button
                            variant="destructive"
                            onClick={() => open('void')}
                        >
                            Void
                        </Button>
                    ) : null
                }
                // a held charge's primary and secondary are Capture and Void, so its refund view is in More actions
                overflow={
                    decidable
                        ? [{ label: 'Refunds', onSelect: viewRefunds }]
                        : undefined
                }
            />
            <div className="space-y-4">
                {c?.state === 'in_review' ? (
                    <p
                        className="text-muted-foreground text-sm"
                        data-testid="open-review-notice"
                    >
                        <Link to="/risk/reviews" className="hover:underline">
                            This payment has an open review. Decide it there.
                        </Link>
                    </p>
                ) : null}
                <ChargeFacts chargeId={id} />
                <ChargeTimeline chargeId={id} />
                <ChargeHttpCalls chargeId={id} />
                <WebhookDeliveries {...subjects} />
            </div>
            {action?.kind === 'capture' && c ? (
                <ConfirmDialog
                    open
                    onOpenChange={(o) => (o ? null : setAction(null))}
                    title="Capture payment"
                    description={
                        <>
                            Capture{' '}
                            <Money
                                cents={c.amount_cents}
                                currency={c.currency}
                            />{' '}
                            from this held card? This charges the customer.
                        </>
                    }
                    confirmLabel="Capture"
                    busy={act.isPending}
                    onConfirm={() => send(action)}
                >
                    {act.error ? (
                        <p role="alert" className="text-destructive text-xs">
                            {apiErrorMessage(act.error)}
                        </p>
                    ) : null}
                </ConfirmDialog>
            ) : null}
            {action?.kind === 'void' ? (
                <ReasonDialog
                    open
                    onOpenChange={(o) => (o ? null : setAction(null))}
                    title="Void payment"
                    description="The hold is released: the customer is not charged."
                    confirmLabel="Void"
                    destructive
                    required
                    busy={act.isPending}
                    error={act.error}
                    onConfirm={(reason) => send(action, reason)}
                />
            ) : null}
            {refunding && c ? (
                <RefundSheet bound={c} onClose={() => setRefunding(false)} />
            ) : null}
            {viewingRefunds && c ? (
                <RefundView
                    charge={c}
                    onClose={() => setViewingRefunds(false)}
                />
            ) : null}
        </div>
    );
}
